import React from 'react';
import * as d3 from 'd3';

import './Plot.css';

export interface NanofiberPlotProps { data: { [key: string]: number[] }, title: string, xLabel: string, yLabel: string }

export class NanofiberPlot extends React.Component<NanofiberPlotProps, {}> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void

    constructor(props: NanofiberPlotProps) {
        super(props)
        this.base = null

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
    }

    componentDidMount() {
        this.renderPlot();
    }

    componentDidUpdate(prevProps: NanofiberPlotProps) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
    }

    renderPlot() {
        if (this.base === null)
            return;
        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        const plot_margin = { top: 10, right: 10, bottom: 40, left: 70 };
        const plot_width = width - plot_margin.left - plot_margin.right;
        const plot_height = height - plot_margin.top - plot_margin.bottom;

        var dataLength = null;
        var minVal = null, maxVal = null;
        for (var key in this.props.data) {
            const curMin = d3.min(this.props.data[key]);
            dataLength = this.props.data[key].length;
            if (curMin !== null && (minVal === null || curMin! < minVal!))
                minVal = curMin;

            const curMax = d3.max(this.props.data[key])
            if (curMax !== null && (maxVal! === null || curMax! > maxVal!))
                maxVal = curMax;
        }

        if (dataLength === null)
            dataLength = 1
        if (minVal === null)
            minVal = 0
        if (maxVal === null)
            maxVal = 1

        const d3time = d3.timeParse('%Q')
        const time = (x: number) => d3time(Math.floor(x) as any)

        const xScale = d3.scaleTime()
            .domain((this.props.data['time'].length === 0) ? [new Date(), new Date()] :
                [time(this.props.data['time'][0])!, time(this.props.data['time'][this.props.data['time'].length - 1])!])
            .range([0, plot_width]);
        const yScale = d3.scaleLinear()
            .domain([minVal!, maxVal!])
            .range([plot_height, 0]);

        const line = d3.line<{ time: number, value: number }>()
            .x(function (d) { return xScale(d.time) as number; })
            .y(function (d) { return yScale(d.value) as number; })

        d3.select(this.base!).select("svg").remove();
        const svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        const xAxis = svg.append('g')
            .attr('class', 'plot-axis bottom')
        xAxis.append('g')
            .attr('transform', `translate(${plot_margin.left},${plot_height + plot_margin.top})`)
            .call(d3.axisBottom(xScale));
        xAxis.append('g')
            .attr('class', 'label')
            .append("text")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "text-after-edge")
            .attr("transform", `translate(${plot_margin.left + plot_width / 2},${height})`)
            .text(this.props.xLabel);

        const yAxis = svg.append('g')
            .attr('class', 'plot-axis left')
        yAxis.append('g')
            .attr('transform', `translate(${plot_margin.left},${plot_margin.top})`)
            .call(d3.axisLeft(yScale));
        yAxis.append('g')
            .attr('class', 'label')
            .append("text")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "hanging")
            .attr("transform", `translate(0,${plot_margin.top + plot_height / 2})rotate(-90)`)
            .text(this.props.yLabel);

        const plot_area = svg.append("g")
            .attr("class", "plot-area")
            .attr('transform', `translate(${plot_margin.left},${plot_margin.top})`)

        for (var key in this.props.data) {
            var mergedata: { value: number, time: number }[] = []
            for (var idx in this.props.data['time'])
                mergedata.push({ time: this.props.data['time'][idx], value: this.props.data[key][idx] })

            plot_area.append("path")
                .datum(mergedata)
                .attr("d", line);
        }
    }

    render() {
        return (
            <div className="Plot">
                <div className="header">
                    <span>{this.props.title}</span>
                </div>
                <div className="plot" ref={this.updateRef} />
            </div>
        )
    }
}

export default NanofiberPlot
